import { useMemo, useState } from 'react';
import { Empty } from './ui.jsx';
import { getState } from '../data/store.js';

/**
 * Find a member by typing.
 *
 * Replaces a raw <select> over every member in the gym. That is workable at
 * fourteen and useless at four hundred: no search, no way to tell two people
 * with the same first name apart, and on a phone it becomes a scroll wheel
 * through the entire roster. This is the same search the front desk already
 * uses, which is also the one staff already know.
 *
 * `exclude` takes ids that cannot be chosen — members already on the roster —
 * so a caller never has to filter the list itself and then explain the gap.
 * `detail` renders per-row context, which is where eligibility goes.
 */
export default function MemberPicker({
  value, onChange, exclude, detail, limit = 8, autoFocus = true, placeholder,
}) {
  const [query, setQuery] = useState('');
  const members = getState().members;
  const blocked = exclude || new Set();

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const pool = members.filter((m) => !blocked.has(m.id));
    if (!q) return pool.slice(0, limit);
    return pool
      .filter((m) => [m.name, m.email, m.phone]
        .filter(Boolean).some((v) => v.toLowerCase().includes(q)))
      .slice(0, limit);
  }, [members, query, blocked, limit]);

  const chosen = value ? members.find((m) => m.id === value) : null;

  return (
    <div className="stack" style={{ gap: 10 }}>
      <input
        type="search"
        value={query}
        autoFocus={autoFocus}
        placeholder={placeholder || 'Search name, email, phone…'}
        aria-label="Search members"
        onChange={(e) => setQuery(e.target.value)}
      />

      {matches.length === 0 ? (
        <Empty>
          {blocked.size > 0 && !query.trim()
            ? 'Everyone is already on this list.'
            : 'No member by that name.'}
        </Empty>
      ) : (
        <div className="picker" role="listbox" aria-label="Members">
          {matches.map((m) => (
            <button
              key={m.id}
              type="button"
              role="option"
              aria-selected={value === m.id}
              className={value === m.id ? 'picker-row selected' : 'picker-row'}
              onClick={() => onChange(m.id)}
            >
              <span className="picker-main">
                <b>{m.name}</b>
                <span className="mono">{m.email || m.phone || ''}</span>
              </span>
              {detail && <span className="picker-detail">{detail(m)}</span>}
            </button>
          ))}
        </div>
      )}

      {chosen && !matches.some((m) => m.id === chosen.id) && (
        <p className="hint">Selected: {chosen.name}</p>
      )}
    </div>
  );
}
